import { db, storage, auth } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc, deleteDoc, getDoc, getDocs, query, where, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { getCategoryName } from '../constants/categories';
import { getEffectiveLimits } from '../constants/limits';

/**
 * Subir imagen de servicio a Firebase Storage
 * @param {File} file - Archivo de imagen
 * @param {string} userId - ID del usuario
 * @returns {Promise<string>} URL de la imagen
 */
const uploadServiceImage = async (file, userId) => {
  try {
    const fileName = `${uuidv4()}_${file.name}`;
    const storageRef = ref(storage, `services/${userId}/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error al subir imagen:', error);
    throw new Error('Error al subir la imagen');
  }
};

/**
 * Crear un nuevo servicio
 * @param {Object} serviceData - Datos del servicio
 * @param {Array<File>} images - Archivos de imagen
 * @returns {Promise<Object>} Resultado de la operación
 */
export const createService = async (serviceData, images = []) => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return {
        success: false,
        error: 'Debes iniciar sesión para crear un servicio'
      };
    }

    // Validaciones
    if (!serviceData.caption || !serviceData.description) {
      return {
        success: false,
        error: 'Título y descripción son obligatorios'
      };
    }

    if (images.length === 0) {
      return {
        success: false,
        error: 'Debes agregar al menos una imagen'
      };
    }

    if (!serviceData.prices || serviceData.prices.length === 0) {
      return {
        success: false,
        error: 'Debes agregar al menos un paquete con precio'
      };
    }

    // Obtener datos actualizados del usuario desde Firestore
    let userDisplayName = currentUser.displayName || 'Usuario';
    let userPhotoURL = currentUser.photoURL || null;
    let isPremium = false;
    
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        userDisplayName = userData.displayName || userData.username || currentUser.displayName || 'Usuario';
        userPhotoURL = userData.photoURL || userData.profileImage || currentUser.photoURL || null;
        isPremium = userData?.isPremium === true;
      }
    } catch (userError) {
      console.warn('No se pudieron obtener datos del usuario desde Firestore, usando datos de Auth:', userError);
    }

    // Límite de servicios activos (enforce real)
    const limits = getEffectiveLimits(isPremium);
    const activeServicesLimit = limits.activeServices;
    if (!isPremium) {
      let activeCount = 0;
      // Preferimos isActive==true (lo setea este mismo createService)
      try {
        const qActive = query(
          collection(db, 'services'),
          where('userId', '==', currentUser.uid),
          where('isActive', '==', true),
          limit(activeServicesLimit + 1)
        );
        const snap = await getDocs(qActive);
        activeCount = snap.size;
      } catch (e) {
        // Fallback por si docs viejos no tienen isActive indexado/usable
        try {
          const qStatus = query(
            collection(db, 'services'),
            where('userId', '==', currentUser.uid),
            where('status', '==', 'active'),
            limit(activeServicesLimit + 1)
          );
          const snap2 = await getDocs(qStatus);
          activeCount = snap2.size;
        } catch {
          // Último fallback: contar por usuario y filtrar cliente (menos eficiente pero seguro)
          const qAny = query(collection(db, 'services'), where('userId', '==', currentUser.uid), limit(activeServicesLimit + 5));
          const snap3 = await getDocs(qAny);
          activeCount = snap3.docs.filter((d) => {
            const s = d.data();
            return s?.isActive === true || s?.status === 'active';
          }).length;
        }
      }

      if (activeCount >= activeServicesLimit) {
        return {
          success: false,
          error: `Límite alcanzado: en cuenta gratuita puedes tener hasta ${activeServicesLimit} servicios activos.`
        };
      }
    }

    console.log('📤 Subiendo imágenes...');
    const imageUrls = [];
    const imagePaths = [];

    for (const image of images) {
      const url = await uploadServiceImage(image, currentUser.uid);
      imageUrls.push(url);
      imagePaths.push(`services/${currentUser.uid}/${image.name}`);
    }

    console.log('✅ Imágenes subidas:', imageUrls.length);

    // Preparar datos del servicio
    const service = {
      // Usuario
      userId: currentUser.uid,
      userName: userDisplayName,
      userPhotoURL: userPhotoURL,
      
      // Contenido
      caption: serviceData.caption,
      description: serviceData.description,
      
      // Media
      media: imageUrls,
      imageUrls: imageUrls,
      imagePaths: imagePaths,
      
      // Categoría
      category: serviceData.category || 'general',
      categoryName: getCategoryName(serviceData.category),
      tags: serviceData.tags || [],
      
      // Precios y paquetes
      servicePrice: serviceData.servicePrice,
      prices: serviceData.prices,
      currency: serviceData.currency || 'ARS',
      
      // Detalles del servicio
      deliveryTime: serviceData.deliveryTime || null,
      specifications: serviceData.specifications || null,
      terms: serviceData.terms || null,
      
      // Opciones
      allowClientDescription: serviceData.allowClientDescription !== false,
      allowCustomizations: serviceData.allowCustomizations !== false,
      
      // Estado
      status: 'active',
      workStatus: 'open',
      isNSFW: serviceData.isNSFW || false,
      isPublic: true,
      isActive: true,
      
      // Contadores
      likesCount: 0,
      commentsCount: 0,
      ordersCount: 0,
      orders: 0,
      views: 0,
      
      // Metadata
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      
      // Flags
      _migrated: false
    };

    console.log('💾 Guardando servicio en Firestore...');
    const docRef = await addDoc(collection(db, 'services'), service);

    // Actualizar con el ID
    await updateDoc(doc(db, 'services', docRef.id), {
      id: docRef.id
    });

    console.log('✅ Servicio creado:', docRef.id);

    // Retornar servicio con timestamp convertido para la UI
    const now = new Date();
    return {
      success: true,
      service: {
        id: docRef.id,
        ...service,
        createdAt: now,
        updatedAt: now
      }
    };
  } catch (error) {
    console.error('❌ Error al crear servicio:', error);
    return {
      success: false,
      error: error.message || 'Error al crear el servicio'
    };
  }
};

/**
 * Actualizar un servicio existente
 * @param {string} serviceId - ID del servicio
 * @param {Object} updates - Campos a actualizar
 * @param {Array<string>} existingImages - URLs de imágenes existentes a mantener
 * @param {Array<File>} newImages - Nuevas imágenes a subir
 * @returns {Promise<Object>} Resultado de la operación
 */
export const updateService = async (serviceId, updates, existingImages = [], newImages = []) => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return {
        success: false,
        error: 'Debes iniciar sesión'
      };
    }

    console.log('📝 Actualizando servicio...');
    
    // Combinar imágenes existentes con nuevas
    let allImageUrls = [...existingImages];
    
    // Subir nuevas imágenes si las hay
    if (newImages && newImages.length > 0) {
      console.log('📤 Subiendo nuevas imágenes...');
      for (const image of newImages) {
        const url = await uploadServiceImage(image, currentUser.uid);
        allImageUrls.push(url);
      }
      console.log('✅ Nuevas imágenes subidas:', newImages.length);
    }

    // Preparar actualización
    const updateData = {
      ...updates,
      media: allImageUrls,
      imageUrls: allImageUrls,
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, 'services', serviceId), updateData);

    console.log('✅ Servicio actualizado:', serviceId);

    // Retornar servicio actualizado para la UI
    const now = new Date();
    return {
      success: true,
      service: {
        id: serviceId,
        ...updates,
        media: allImageUrls,
        imageUrls: allImageUrls,
        updatedAt: now
      }
    };
  } catch (error) {
    console.error('Error al actualizar servicio:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Eliminar un servicio
 * @param {string} serviceId - ID del servicio
 * @returns {Promise<Object>} Resultado de la operación
 */
export const deleteService = async (serviceId) => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return {
        success: false,
        error: 'Debes iniciar sesión'
      };
    }

    // TODO: Eliminar imágenes de Storage también
    
    await deleteDoc(doc(db, 'services', serviceId));

    return {
      success: true
    };
  } catch (error) {
    console.error('Error al eliminar servicio:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
